import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import type { AnnouncementView } from "@portal/schemas";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  Textarea,
} from "@portal/ui";
import { useMemo, useState, type ReactNode } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { createAnnouncement, updateAnnouncement } from "../../api/client";
import { useAnnouncements, useInvalidate } from "../../api/queries";
import { CardStatus } from "../../components/cards/CardStatus";
import { DataTable, createDataTableColumns } from "../../components/DataTable";
import { InlineError } from "../../components/InlineError";
import { CardTitleWithHint } from "../../components/Hint";
import { Eye, EyeOff, Pencil, Plus, Save } from "lucide-react";

const fmt = new Intl.DateTimeFormat("ja-JP", { dateStyle: "medium", timeStyle: "short" });

/** 画面用: 日時は input[type=datetime-local] の文字列(ローカル時刻)で持ち、送信時に ISO へ変換する。 */
const formSchema = z
  .object({
    title: z.string().trim().min(1, "タイトルは必須です").max(200),
    body: z.string().max(5000),
    publishFrom: z.string().min(1, "掲載開始は必須です"),
    publishUntil: z.string(),
    pinned: z.boolean(),
  })
  .refine((v) => !v.publishUntil || new Date(v.publishUntil) > new Date(v.publishFrom), {
    message: "掲載終了は掲載開始より後にしてください",
    path: ["publishUntil"],
  });
type FormValues = z.infer<typeof formSchema>;

/** ISO → datetime-local 値(ローカル時刻、分まで)。 */
export function toLocalInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
const toIso = (local: string) => new Date(local).toISOString();

function stateOf(
  a: AnnouncementView,
  now: Date,
): { label: string; variant: "success" | "outline" | "info" } {
  if (a.archived) return { label: "取り下げ", variant: "outline" };
  if (new Date(a.publishFrom) > now) return { label: "掲載予定", variant: "info" };
  if (a.publishUntil && new Date(a.publishUntil) <= now)
    return { label: "掲載終了", variant: "outline" };
  return { label: "掲載中", variant: "success" };
}

const col = createDataTableColumns<AnnouncementView>();
const ANNOUNCEMENT_FILTERS = [
  { columnId: "state", label: "状態" },
  { columnId: "author", label: "作成者" },
] as const;

/** お知らせの管理(SPEC §13.4)。SYSTEM_ADMIN のみ。取り下げは論理削除。 */
export function AdminAnnouncementsPage() {
  const q = useAnnouncements(true);
  const invalidate = useInvalidate();
  const archive = useMutation({
    mutationFn: (a: AnnouncementView) =>
      updateAnnouncement(a.id, { revision: a.revision, archived: !a.archived }),
    onSuccess: () => invalidate.company(),
  });
  const columns = useMemo(() => {
    const now = new Date();
    return col.columns([
      col.accessor("title", {
        header: "タイトル",
        meta: { className: "font-medium" },
        cell: (info) => (
          <>
            {info.row.original.pinned && (
              <Badge variant="secondary" className="mr-2">
                固定
              </Badge>
            )}
            {info.getValue()}
          </>
        ),
      }),
      col.accessor("body", { header: "本文", meta: { className: "hidden" }, enableSorting: false }),
      col.accessor("publishFrom", {
        header: "掲載期間",
        meta: { className: "text-muted-foreground" },
        cell: (info) => {
          const a = info.row.original;
          return (
            <>
              {fmt.format(new Date(a.publishFrom))}
              {" 〜 "}
              {a.publishUntil ? fmt.format(new Date(a.publishUntil)) : "無期限"}
            </>
          );
        },
      }),
      col.accessor("authorName", { id: "author", header: "作成者" }),
      col.accessor((a) => stateOf(a, now).label, {
        id: "state",
        header: "状態",
        cell: (info) => (
          <Badge variant={stateOf(info.row.original, now).variant}>{info.getValue()}</Badge>
        ),
      }),
      col.display({
        id: "actions",
        header: "",
        meta: { className: "w-24" },
        cell: (info) => {
          const a = info.row.original;
          return (
            <div className="flex gap-2">
              <AnnouncementFormDialog
                announcement={a}
                trigger={
                  <Button variant="ghost" size="icon-sm" aria-label="編集" title="編集">
                    <Pencil />
                  </Button>
                }
              />
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => archive.mutate(a)}
                disabled={archive.isPending}
                aria-label={a.archived ? "戻す" : "取り下げる"}
                title={a.archived ? "戻す" : "取り下げる"}
              >
                {a.archived ? <Eye /> : <EyeOff />}
              </Button>
            </div>
          );
        },
      }),
    ]);
  }, [archive]);
  return (
    <Card data-testid="admin-announcements-card">
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitleWithHint hint="掲載期間内だけ、全員のナビゲーションバーのベルに出ます。掲載終了を空にすると無期限です。">
            お知らせ管理
          </CardTitleWithHint>
        </div>
        <AnnouncementFormDialog
          trigger={
            <Button>
              <Plus />
              お知らせを追加
            </Button>
          }
        />
      </CardHeader>
      <CardContent>
        <CardStatus isPending={q.isPending} isError={q.isError} refetch={() => q.refetch()} />
        <InlineError error={archive.error} />
        {q.data && (
          <DataTable
            columns={columns}
            data={q.data.announcements}
            getRowId={(a) => a.id}
            emptyText="お知らせはまだありません。"
            searchPlaceholder="タイトル・本文・作成者で検索"
            filters={ANNOUNCEMENT_FILTERS}
          />
        )}
      </CardContent>
    </Card>
  );
}

function AnnouncementFormDialog({
  announcement,
  trigger,
}: {
  announcement?: AnnouncementView;
  trigger: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const invalidate = useInvalidate();
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    values: {
      title: announcement?.title ?? "",
      body: announcement?.body ?? "",
      publishFrom: toLocalInput(announcement?.publishFrom ?? new Date().toISOString()),
      publishUntil: toLocalInput(announcement?.publishUntil ?? null),
      pinned: announcement?.pinned ?? false,
    },
  });
  const save = useMutation({
    mutationFn: (v: FormValues) => {
      const body = {
        title: v.title,
        body: v.body,
        publishFrom: toIso(v.publishFrom),
        publishUntil: v.publishUntil ? toIso(v.publishUntil) : null,
        pinned: v.pinned,
      };
      return announcement
        ? updateAnnouncement(announcement.id, { ...body, revision: announcement.revision })
        : createAnnouncement(body);
    },
    onSuccess: async () => {
      await invalidate.company();
      setOpen(false);
      if (!announcement) form.reset();
    },
  });
  const errors = form.formState.errors;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <form
          onSubmit={form.handleSubmit((v) => save.mutate(v))}
          noValidate
          className="flex flex-col gap-4"
        >
          <DialogHeader>
            <DialogTitle>{announcement ? "お知らせを編集" : "お知らせを追加"}</DialogTitle>
            <DialogDescription>本文は改行だけが反映されます(HTML 不可)。</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ann-title">タイトル</Label>
            <Input id="ann-title" {...form.register("title")} />
            {errors.title && <p className="text-xs text-destructive">{errors.title.message}</p>}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ann-body">本文</Label>
            <Textarea id="ann-body" rows={4} {...form.register("body")} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ann-from">掲載開始</Label>
              <Input id="ann-from" type="datetime-local" {...form.register("publishFrom")} />
              {errors.publishFrom && (
                <p className="text-xs text-destructive">{errors.publishFrom.message}</p>
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ann-until">掲載終了</Label>
              <Input id="ann-until" type="datetime-local" {...form.register("publishUntil")} />
              {errors.publishUntil && (
                <p className="text-xs text-destructive">{errors.publishUntil.message}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Controller
              control={form.control}
              name="pinned"
              render={({ field }) => (
                <Checkbox
                  id="ann-pinned"
                  checked={field.value}
                  onCheckedChange={(c) => field.onChange(c === true)}
                />
              )}
            />
            <Label htmlFor="ann-pinned">先頭に固定する</Label>
          </div>
          <InlineError error={save.error} />
          <DialogFooter>
            <Button type="submit" disabled={save.isPending}>
              <Save />
              保存
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
